import React from 'react';
import Editor from '@monaco-editor/react';
import styled from 'styled-components';
import { Spin } from 'antd';

/**
 * CodeEditor - Reusable code editor component with syntax highlighting
 * Powered by Monaco Editor (VS Code's editor)
 *
 * @param {string} language - Programming language ('json', 'html', 'javascript', 'markdown')
 * @param {string} value - Editor content
 * @param {function} onChange - Callback when content changes
 * @param {string} height - Editor height (default: '400px')
 * @param {boolean} readOnly - Whether editor is read-only (default: false)
 * @param {string} theme - Editor theme ('vs-dark' or 'light', default: 'vs-dark')
 * @param {object} options - Additional Monaco editor options
 */
const CodeEditor = ({
  language = 'json',
  value = '',
  onChange,
  height = '400px',
  readOnly = false,
  theme = 'vs-dark',
  options = {},
}) => {
  const defaultOptions = {
    minimap: { enabled: false },
    fontSize: 14,
    lineNumbers: 'on',
    formatOnPaste: true,
    formatOnType: true,
    scrollBeyondLastLine: false,
    automaticLayout: true,
    tabSize: 2,
    wordWrap: 'on',
    readOnly,
    ...options,
  };

  const handleEditorChange = (newValue) => {
    if (onChange && !readOnly) {
      onChange(newValue);
    }
  };

  const handleEditorValidation = (markers) => {
    // Log validation errors for debugging
    if (markers.length > 0) {
      console.warn('Editor validation errors:', markers);
    }
  };

  return (
    <EditorContainer>
      <Editor
        height={height}
        language={language}
        value={value}
        onChange={handleEditorChange}
        theme={theme}
        options={defaultOptions}
        onValidate={handleEditorValidation}
        loading={<LoadingContainer><Spin size="large" /></LoadingContainer>}
      />
    </EditorContainer>
  );
};

const EditorContainer = styled.div`
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  overflow: hidden;

  &:hover {
    border-color: #40a9ff;
  }

  &:focus-within {
    border-color: #40a9ff;
    box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  background: #1e1e1e;
`;

export default CodeEditor;
